// src/hooks/useAuth.js
import { useState, useEffect, useCallback } from 'react';
import { tokenStorage, userStorage } from '../utils/storage';
import { authService } from '../services/auth';
import { useNotificationCenter } from '../context/NotificationCenterContext';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addNotification: addToCenter } = useNotificationCenter();

  // Initialize auth state
  const initAuth = useCallback(() => {
    try {
      const storedToken = tokenStorage.getToken();
      const storedUser = userStorage.getUser();

      if (storedToken && tokenStorage.isTokenValid()) {
        setToken(storedToken);
        setUser(storedUser);
      } else {
        // Clear invalid data
        tokenStorage.removeToken();
        userStorage.removeUser();
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      setError('Failed to initialize authentication');
    } finally {
      setLoading(false);
    }
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        const { token: newToken, user: userData } = response.data;
        
        tokenStorage.setToken(newToken);
        userStorage.setUser(userData);
        
        setToken(newToken);
        setUser(userData);
        
        // Add to notification center
        addToCenter({
          type: 'login',
          title: 'Login Successful',
          message: `Welcome back, ${userData.name}!`
        });
        
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.register(userData);
      
      if (response.success && response.data) {
        const { token: newToken, user: newUser } = response.data;
        
        // Store in localStorage
        tokenStorage.setToken(newToken);
        userStorage.setUser(newUser);
        
        // Update state
        setToken(newToken);
        setUser(newUser);
        
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);
      
      // Call logout API if token exists
      if (token) {
        try {
          await authService.logout();
        } catch (error) {
          console.warn('Logout API call failed:', error);
          // Continue with local logout even if API fails
        }
      }
      
      // Clear storage
      tokenStorage.removeToken();
      userStorage.removeUser();
      
      // Clear state
      setToken(null);
      setUser(null);
      setError(null);
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Update profile function
  const updateProfile = async (updates) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.updateProfile(updates);
      
      if (response.success && response.data) {
        const updatedUser = response.data;
        
        // Update storage
        userStorage.setUser(updatedUser);
        
        // Update state
        setUser(updatedUser);
        
        return { success: true, data: updatedUser };
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Profile update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Change password function
  const changePassword = async (passwordData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.changePassword(passwordData);
      
      if (response.success) {
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Password change failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Get current user info
  const getCurrentUser = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.getCurrentUser();
      
      if (response.success && response.data) {
        const userData = response.data;
        
        // Update storage
        userStorage.setUser(userData);
        
        // Update state
        setUser(userData);
        
        return { success: true, data: userData };
      } else {
        throw new Error(response.message || 'Failed to get user data');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get user data';
      setError(errorMessage);
      
      // If token is invalid, logout
      if (error.response?.status === 401) {
        logout();
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Check if user is authenticated
  const isAuthenticated = token && user && tokenStorage.isTokenValid();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Check if user is staff
  const isStaff = user?.role === 'staff' || user?.role === 'admin';

  // Initialize on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Auto-refresh user data periodically
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        if (tokenStorage.isTokenValid()) {
          getCurrentUser();
        } else {
          logout();
        }
      }, 15 * 60 * 1000); // Check every 15 minutes

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  return {
    // State
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isStaff,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    getCurrentUser,
    
    // Utils
    clearError: () => setError(null)
  };
};